import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import styled, { createGlobalStyle } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    --headerTopHeight: ${fluid(68, 102, Device.TabletLarge)};
    --headerTopHeightReduced: ${fluid(52, 76, Device.TabletLarge)};
    --headerNavHeight: 0px;
    --headerCtaHeight: 0px;

    @media ${from(Device.Tablet)} {
      --headerCtaHeight: ${fluid(32, 42, Device.TabletLarge)};
    }

    @media ${from(Device.TabletLarge)} {
      --headerNavHeight: 42px;
    }

    --headerHeight: calc(var(--headerTopHeight, 0px) + var(--headerCtaHeight, 0px) + var(--headerNavHeight, 0px) + var(--headerMessageHeight, 0px));
    --headerScrollHeight: calc(var(--headerTopHeightReduced, 0px) + var(--headerCtaHeight, 0px) + var(--headerNavHeight, 0px) + var(--headerMessageHeight, 0px)); 
  }

  #root {
    padding-top: var(--headerHeight);
  }

  @media print {
    :root {
      --headerTopHeight: 0px;
      --headerNavHeight: 0px;
      --headerCtaHeight: 0px;
    }

    #root {
      padding-top: 0;
    }
  }
`;
const Container = styled.header `
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  z-index: ${Layers.Header};

  @media print {
    display: none;
  }
`;
const TopContainer = styled.div `
  ${transition('height')};

  display: grid;
  background: ${brand.beige};
  color: ${brand.black};
  height: var(--headerTopHeight);
  z-index: 2;

  [data-scroll-active='true'] & {
    height: var(--headerTopHeightReduced);
  }
`;
const TopInner = styled.div `
  ${siteWide}

  --siteWidth: 1550px;

  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;

  @media ${from(Device.TabletLarge)} {
    width: 100%;
    max-width: var(--maxWidth);
  }
`;
const Logo = styled.img `
  ${transition('max-width')};

  max-width: ${fluid(170, 308)};

  [data-scroll-active='true'] & {
    max-width: ${fluid(150, 264)};
  }
`;
const QuickLinkList = styled.ul `
  display: flex;
  gap: ${fluid(16, 40, Device.TabletLarge)};
  align-items: center;
  width: calc((100% - ${fluid(170, 308)}) / 2);

  &:last-of-type {
    justify-content: flex-end;
  }

  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const QuickLinkItem = styled.li ``;
const QuickLinkAnchor = styled.a `
  display: flex;
  align-items: center;
  background: none;
  border: 0;
  color: currentColor;
  ${fonts.brandonGrotesque.bold};
  font-size: ${fluid(16, 18, Device.TabletLarge)};
  margin: 0;
  padding: 0;
  text-decoration: none;

  &:focus,
  &:hover {
    text-decoration: underline;
  }
`;
const QuickLinkIcon = styled.img `
  display: inline-block;
  height: ${fluid(16, 18, Device.TabletLarge)};
  margin-right: ${fluid(5, 10)};
  object-fit: contain;
  object-position: center;
  width: ${fluid(16, 18, Device.TabletLarge)};

  [data-search-active='true'] & {
    height: ${fluid(12, 14, Device.TabletLarge)};
    width: ${fluid(12, 14, Device.TabletLarge)};
    margin: ${fluid(2, 3)};
    margin-right: ${fluid(7, 13)};
  }
`;
const NavigationContainer = styled.div `
  ${transition('height')};

  display: grid;
  background: ${brand.green};
  color: ${brand.white};
  height: var(--headerNavHeight);
  z-index: 1;

  @media ${until(Device.TabletLarge)} {
    display: none;
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-reduce="true"] & {
      height: 0;
      overflow: hidden;
    }
  }
`;
const CtaContainer = styled.div `
  ${transition('transform')};

  display: flex;
  background: ${brand.white};
  gap: 1px;
  height: var(--headerCtaHeight);
  z-index: 0;

  @media ${until(Device.Tablet)} {
    display: none;
  }

  @media ${until(Device.TabletLarge)} {
    order: -1;
  }
`;
const FloatingCtaContainer = styled.div `
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  display: block;
  z-index: ${Layers.Header};

  @media ${from(Device.Tablet)} {
    display: none;
  }
`;
const Cta = styled.a `
  ${fonts.brandonGrotesque.bold};

  display: inline-flex;
  background: ${brand.black};
  color: ${brand.white};
  flex: 1 1 calc(100% / var(--count));
  font-size: 14px;
  line-height: 1em;
  place-content: center;
  place-items: center;
  text-decoration: none;
  text-transform: uppercase;
  text-underline-position: under;

  &:focus,
  &:hover {
    text-decoration: underline;
  }

  @media ${until(Device.TabletLarge)} {
    display: none;

    &:nth-child(2) {
      display: inline-flex;
    }
  }
`;
const MobileSearch = styled.button `
  --size: 25px;
  --tapSize: 48px;

  display: block;
  background: none;
  border: 0;
  height: var(--tapSize);
  margin: 0;
  padding: calc((var(--tapSize) - var(--size)) / 2);
  width: var(--tapSize);

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const MobileSearchIcon = styled.span `
  display: block;
  height: 100%;
  width: 100%;
`;
const MobileNav = styled.button `
  display: block;
  background: none;
  border: 0;
  margin: 0;
  padding: 0;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const HeaderStyles = {
    Container,
    Cta,
    CtaContainer,
    FloatingCtaContainer,
    GlobalStyles,
    Logo,
    MobileNav,
    MobileSearch,
    MobileSearchIcon,
    NavigationContainer,
    QuickLinkAnchor,
    QuickLinkIcon,
    QuickLinkItem,
    QuickLinkList,
    TopContainer,
    TopInner,
};
export default HeaderStyles;
